import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Calendar, CheckSquare, ExternalLink } from 'lucide-react';
import { todoApi, TodoList } from '../../api/client';
import { cn } from '../../utils/cn';

interface AddToTodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  messageId: string;
  mailSubject: string;
  mailFrom: string;
  provider: 'microsoft' | 'google';
}

export function AddToTodoModal({
  isOpen,
  onClose,
  accountId,
  messageId,
  mailSubject,
  mailFrom,
  provider,
}: AddToTodoModalProps) {
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const queryClient = useQueryClient();
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle(`📧 ${mailSubject}`);
      setDueDate('');
    }
  }, [isOpen, mailSubject]);
  
  // Fetch To-Do lists
  const { data: listsData, isLoading: loadingLists, error: listsError } = useQuery({
    queryKey: ['todoLists', accountId],
    queryFn: () => todoApi.getLists(accountId),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });
  
  const lists = listsData?.lists || [];
  
  // Auto-select default list
  useEffect(() => {
    if (lists.length > 0 && !selectedListId) {
      const defaultList = lists.find(l => l.isDefault) || lists[0];
      setSelectedListId(defaultList.id);
    }
  }, [lists, selectedListId]);
  
  // Create task mutation
  const createMutation = useMutation({
    mutationFn: () => todoApi.createFromMail(accountId, {
      messageId,
      listId: selectedListId,
      title: title.trim(),
      dueDate: dueDate || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todoTasks', accountId] });
      onClose();
    },
  });
  
  if (!isOpen) return null;
  
  const providerName = provider === 'microsoft' ? 'Microsoft To-Do' : 'Google Tasks';
  const providerIcon = provider === 'microsoft' ? '🔷' : '🔵';
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="w-full max-w-md rounded-xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-4"
          style={{ 
            backgroundColor: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          <div className="flex items-center gap-3">
            <span className="text-xl">{providerIcon}</span>
            <div>
              <h2 
                className="font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                Zu {providerName} hinzufügen
              </h2>
              <p 
                className="text-xs"
                style={{ color: 'var(--text-tertiary)' }}
              >
                Aufgabe aus E-Mail erstellen
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Mail Info */}
          <div 
            className="p-3 rounded-lg text-sm"
            style={{ 
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-default)',
            }}
          >
            <p 
              className="font-medium truncate"
              style={{ color: 'var(--text-primary)' }}
            >
              {mailSubject}
            </p>
            <p 
              className="text-xs truncate mt-1"
              style={{ color: 'var(--text-tertiary)' }}
            >
              Von: {mailFrom}
            </p>
          </div>
          
          {loadingLists ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--brand-primary)' }} />
            </div>
          ) : listsError ? (
            <div 
              className="p-4 rounded-lg text-center"
              style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: 'var(--color-error)',
              }}
            >
              <p className="text-sm font-medium">Fehler beim Laden der Listen</p>
              <p className="text-xs mt-1">
                Möglicherweise fehlen die To-Do Berechtigungen. 
                Bitte Mail-Konto neu verbinden.
              </p>
            </div>
          ) : (
            <>
              {/* List Selection */}
              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <CheckSquare className="w-4 h-4 inline mr-2" />
                  Liste auswählen
                </label>
                <select
                  value={selectedListId}
                  onChange={e => setSelectedListId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                  }}
                >
                  {lists.map(list => (
                    <option key={list.id} value={list.id}>
                      {list.name} {list.isDefault ? '(Standard)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Task Title */}
              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Aufgaben-Titel
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Aufgabe..."
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                  }}
                />
              </div>
              
              {/* Due Date */}
              <div>
                <label 
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Fälligkeitsdatum (optional)
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                  }}
                />
              </div>
              
              {/* Info */}
              <div 
                className="flex items-start gap-2 p-3 rounded-lg text-xs"
                style={{ 
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  color: 'var(--text-secondary)',
                }}
              >
                <ExternalLink className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--brand-primary)' }} />
                <p>
                  Die Aufgabe enthält automatisch einen Link zur Original-E-Mail, 
                  damit du sie direkt in {provider === 'microsoft' ? 'Outlook' : 'Gmail'} öffnen kannst.
                </p>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div 
          className="flex justify-end gap-3 px-6 py-4"
          style={{ 
            backgroundColor: 'var(--bg-secondary)',
            borderTop: '1px solid var(--border-default)',
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ 
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            Abbrechen
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!selectedListId || !title.trim() || createMutation.isPending}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            style={{ 
              backgroundColor: 'var(--brand-primary)',
              color: 'white',
            }}
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Erstelle...
              </>
            ) : (
              <>
                <CheckSquare className="w-4 h-4" />
                Aufgabe erstellen
              </>
            )}
          </button>
        </div>
        
        {/* Error Message */}
        {createMutation.isError && (
          <div 
            className="px-6 pb-4 text-sm"
            style={{ color: 'var(--color-error)' }}
          >
            Fehler: {(createMutation.error as Error)?.message || 'Unbekannter Fehler'}
          </div>
        )}
      </div>
    </div>
  );
}
